import { Box } from "@mui/material"
import {
  FocusContext,
  FocusHandler,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation"
import React, {
  forwardRef,
  ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react"
import { useTransitions } from "src/hooks/use-transitions"

import { useEventBind } from "../../hooks/useEventBind"
import { mouseHoverScroll, s } from "../../utilities/common"
import { fontSizeFactorStep, LGMouseEvent } from "../../utilities/constants"
import { theme, TI } from "../../utilities/theme"
import { DGText } from "../common/DGText"

import { LiveTabTitle, LiveTitleRefProps } from "./LiveTabTitle"
import { TeaserDimension, TeaserDimensions } from "./Slider"

interface Props {
  title: string
  margin?: boolean
  children: ReactNode
  onFocus?: FocusHandler
  onBlur?: FocusHandler
  liveTitles?: Record<number, string>
  dimensions: TeaserDimension
}

export type SliderRef = {
  element: HTMLElement
  setIndex: (index: number) => void
}
export const GenericSlider = forwardRef<SliderRef, Props>(
  (
    { title, children, margin = true, onFocus, onBlur, liveTitles, dimensions },
    ref,
  ) => {
    useImperativeHandle(ref, () => {
      return {
        element: scrollView.current as HTMLElement,
        setIndex: (t) => {
          liveTitlesRef.current?.setIndex(t)
        },
      }
    })
    const liveTitlesRef = useRef<LiveTitleRefProps>(null)

    const scrollView = useRef<HTMLElement>(null)
    const {
      ref: fRef,
      focusKey,
      hasFocusedChild,
    } = useFocusable({
      onBlur,
      onFocus,
      isFocusBoundary: true,
      focusBoundaryDirections: ["left", "right"],
      trackChildren: true,
    })

    const { width, colSpacing } = useMemo(() => {
      return (
        TeaserDimensions[dimensions] ?? { width: 0, height: 0, imageHeight: 0 }
      )
    }, [dimensions])

    // DGAPP-6204 stop scrolling if mouse hides while scrolling
    const onStateChange = useCallback((e: LGMouseEvent) => {
      if (!isScrolling.current || e.detail.visibility) return
      isScrolling.current = undefined
    }, [])
    useEventBind(document, "cursorStateChange", onStateChange)

    const isScrolling = useRef<undefined | "left" | "right">(null)
    useEffect(() => {
      let interval: NodeJS.Timer
      const scrollAmount = parseInt(colSpacing, 10) + width
      if (hasFocusedChild) {
        interval = setInterval(
          () =>
            mouseHoverScroll({
              scrollAmount,
              scrollView: scrollView.current,
              direction: isScrolling.current,
              setIndex: liveTitlesRef.current?.setIndex,
            }),
          250,
        )
      }
      return () => {
        if (interval) clearInterval(interval)
      }
    }, [hasFocusedChild])

    const transition = useTransitions()

    return (
      <Box
        onMouseMove={(e) =>
          (isScrolling.current =
            e.clientX < s(50)
              ? "left"
              : e.clientX > s(1920 - 50)
                ? "right"
                : undefined)
        }
        onMouseLeave={() => (isScrolling.current = undefined)}
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          mb: margin ? s(fontSizeFactorStep > 3 ? "80px" : "144px") : undefined,
        }}
      >
        {!liveTitles ? (
          <DGText
            tx={TI.H07tv}
            sx={{
              textTransform: "uppercase",
              color: theme.palette.white.main,
              pl: s("96px"),
              mb: s("64px"),
            }}
          >
            {title}
          </DGText>
        ) : liveTitles ? (
          <LiveTabTitle
            ref={liveTitlesRef}
            liveTitles={liveTitles}
            dimensions={dimensions}
          />
        ) : null}
        <Box sx={{ position: "relative" }}>
          <Box
            ref={scrollView}
            style={{ transition, willChange: "transform" }}
            sx={{
              width: "min-content",
              overflowY: "visible",
            }}
          >
            <FocusContext.Provider value={focusKey}>
              <Box
                ref={fRef}
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  width: "min-content",
                  alignItems: "center",
                  height: "auto",
                  pb: s("2px"),
                  pt: s("2px"),
                  pr: s("96px"),
                  pl: s("96px"),
                  // make sure short sliders aren't skipped from focus priority
                  minWidth: "80vw",
                }}
              >
                {children}
              </Box>
            </FocusContext.Provider>
          </Box>
        </Box>
      </Box>
    )
  },
)
